import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Star, Phone, Heart, Shield, Award, Users, CheckCircle, BadgeCheck, Instagram, Facebook } from "lucide-react";
import { Button } from "../components/ui/button";
import { services, coreValues, companyInfo } from "../data/mockData";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Home = () => {
  const [homeTestimonials, setHomeTestimonials] = useState([]);

  useEffect(() => {
    // Seed data on first load
    axios.post(`${API}/seed`).catch(() => {});
    // Fetch approved testimonials for home preview
    axios.get(`${API}/testimonials`)
      .then((res) => setHomeTestimonials(res.data || []))
      .catch(() => setHomeTestimonials([]));
  }, []);

  const iconMap = {
    Heart: Heart,
    Shield: Shield,
    Award: Award,
    Users: Users,
  };

  return (
    <div data-testid="home-page" className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-24 pb-16">
        {/* Background decoration */}
        <div className="absolute top-20 right-0 w-96 h-96 bg-secondary/30 rounded-full blur-3xl -z-10" />
        <div className="absolute bottom-20 left-0 w-72 h-72 bg-primary/10 rounded-full blur-3xl -z-10" />
        
        <div className="section-container w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="order-2 lg:order-1"
            >
              <span className="inline-block text-xs tracking-widest uppercase font-bold text-primary/60 mb-4">
                Domiciliary Care Tipton
              </span>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold text-primary leading-tight mb-6">
                Caring for you, in the comfort of{" "}
                <span className="text-accent">your home</span>
              </h1>
              <p className="text-muted-foreground text-lg leading-relaxed mb-8 max-w-lg">
                Konastone Healthcare Ltd provides compassionate, person-centred home care 
                services that promote independence, dignity, and comfort for individuals 
                in Tipton and the West Midlands.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <Link to="/contact">
                  <Button
                    data-testid="hero-cta-primary"
                    className="bg-accent hover:bg-accent/90 text-white rounded-full px-8 py-6 text-lg font-medium transition-all hover:scale-105 shadow-lg hover:shadow-xl w-full sm:w-auto"
                  >
                    Book Free Assessment
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <a href={`tel:${companyInfo.phone}`}>
                  <Button
                    data-testid="hero-cta-secondary"
                    variant="outline"
                    className="bg-white text-primary border-2 border-primary/20 hover:border-primary hover:bg-primary/5 rounded-full px-8 py-6 text-lg font-medium transition-all w-full sm:w-auto"
                  >
                    <Phone className="mr-2 w-5 h-5" />
                    Call Us Now
                  </Button>
                </a>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap items-center gap-6">
                <div className="flex items-center gap-2">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <span className="text-sm font-medium text-muted-foreground">
                    15 Reviews on Homecare.co.uk
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <BadgeCheck className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium text-muted-foreground">
                    CQC Rated Good
                  </span>
                </div>
              </div>

              {/* Social Links */}
              <div className="flex items-center gap-4 mt-6">
                <a
                  href={companyInfo.socialMedia.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  <Facebook className="w-5 h-5" />
                  Facebook
                </a>
                <a
                  href={companyInfo.socialMedia.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
                >
                  <Instagram className="w-5 h-5" />
                  Instagram
                </a>
              </div>
            </motion.div>

            {/* Right - Logo Display */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="order-1 lg:order-2 relative flex items-center justify-center"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_915f609f-ac7a-45cc-b95f-47f255f32234/artifacts/wbx3meld_image.png"
                alt="Konastone Healthcare"
                data-testid="hero-logo"
                className="w-full max-w-[500px] h-auto drop-shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CQC Rating & Awards Section */}
      <section className="py-16 bg-primary/5">
        <div className="section-container">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* CQC Rating */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="bg-white border border-primary/10 rounded-2xl p-8 text-center"
            >
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <BadgeCheck className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="font-serif text-2xl font-semibold text-primary mb-2">CQC Rating: Good</h3>
              <p className="text-muted-foreground mb-4">Care Quality Commission Inspection</p>
              <div className="grid grid-cols-5 gap-2 max-w-md mx-auto">
                {["Safe", "Effective", "Caring", "Responsive", "Well-led"].map((item) => (
                  <div key={item} className="text-center">
                    <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-1">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    </div>
                    <span className="text-xs text-muted-foreground">{item}</span>
                  </div>
                ))}
              </div>
              <a
                href="https://www.cqc.org.uk/location/1-2950714022"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block mt-4 text-sm text-primary hover:underline"
              >
                View CQC Report →
              </a>
            </motion.div>

            {/* Awards */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="bg-white border border-primary/10 rounded-2xl p-8 text-center"
            >
              <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-10 h-10 text-yellow-600" />
              </div>
              <h3 className="font-serif text-2xl font-semibold text-primary mb-2">Award-Winning Care</h3>
              <p className="text-muted-foreground mb-6">Recognised for Excellence</p>
              <div className="space-y-3">
                <div className="bg-primary/5 rounded-lg p-3">
                  <p className="font-medium text-primary">Top 20 Home Care Provider</p>
                  <p className="text-sm text-muted-foreground">West Midlands 2019</p>
                </div>
                <div className="bg-primary/5 rounded-lg p-3">
                  <p className="font-medium text-primary">Top 20 Home Care Provider</p>
                  <p className="text-sm text-muted-foreground">West Midlands 2018</p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="py-20 bg-white relative grain-overlay">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <span className="text-xs tracking-widest uppercase font-bold text-primary/60 mb-3 block">
              Why Choose Us
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-primary mb-4">
              Our Core Values
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              At Konastone Healthcare, our values guide everything we do, 
              ensuring exceptional care for every individual we serve.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {coreValues.map((value, index) => {
              const IconComponent = iconMap[value.icon];
              return (
                <motion.div
                  key={value.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  data-testid={`value-card-${index}`}
                  className="bg-background/50 border border-primary/10 rounded-xl p-6 text-center hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
                >
                  <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    {IconComponent && <IconComponent className="w-7 h-7 text-primary" />}
                  </div>
                  <h3 className="font-serif font-semibold text-primary mb-2">{value.title}</h3>
                  <p className="text-sm text-muted-foreground">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-20">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <span className="text-xs tracking-widest uppercase font-bold text-primary/60 mb-3 block">
              Home Care Services
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-primary mb-4">
              How We Can Help
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              From personal care to companionship, we offer a comprehensive range 
              of services tailored to your unique needs.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.slice(0, 6).map((service, index) => (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                data-testid={`service-preview-${service.id}`}
                className="service-card group"
              >
                <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
                  <Heart className="w-6 h-6 text-primary" />
                </div>
                <h3 className="font-serif font-semibold text-primary text-lg mb-2">
                  {service.title}
                </h3>
                <p className="text-muted-foreground text-sm leading-relaxed">
                  {service.description}
                </p>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-center mt-12"
          >
            <Link to="/services">
              <Button
                data-testid="view-all-services-btn"
                className="bg-primary hover:bg-primary/90 text-white rounded-full px-8 py-6 text-lg font-medium transition-all hover:scale-105"
              >
                View All Services
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        <div className="section-container relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-serif text-3xl md:text-4xl font-semibold text-white mb-6">
                Ready to discuss your care needs?
              </h2>
              <p className="text-white/80 text-lg mb-8">
                Book a free, no-obligation care assessment today. Our friendly team 
                will visit you at home to understand your requirements and create a 
                personalized care plan.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/contact">
                  <Button
                    data-testid="cta-book-btn"
                    className="bg-accent hover:bg-accent/90 text-white rounded-full px-8 py-6 text-lg font-medium transition-all hover:scale-105 w-full sm:w-auto"
                  >
                    Book Assessment
                  </Button>
                </Link>
                <a href={`tel:${companyInfo.phone}`}>
                  <Button
                    data-testid="cta-call-btn"
                    variant="outline"
                    className="bg-transparent text-white border-2 border-white/30 hover:border-white hover:bg-white/10 rounded-full px-8 py-6 text-lg font-medium transition-all w-full sm:w-auto"
                  >
                    <Phone className="mr-2 w-5 h-5" />
                    {companyInfo.phone}
                  </Button>
                </a>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="hidden lg:block"
            >
              <img
                src="https://images.unsplash.com/photo-1447069387593-a5de0862481e?w=600"
                alt="Caring companionship"
                className="w-full h-[350px] object-cover rounded-2xl shadow-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Preview */}
      <section className="py-20 bg-white">
        <div className="section-container">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <span className="text-xs tracking-widest uppercase font-bold text-primary/60 mb-3 block">
              Testimonials
            </span>
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-primary mb-4">
              What Our Clients Say
            </h2>
            <div className="flex items-center justify-center gap-2 mb-4">
              {[...Array(5)].map((_, i) => (
                <Star key={i} className="w-6 h-6 text-yellow-400 fill-current" />
              ))}
            </div>
            <p className="text-muted-foreground">
              Rated 5.0 stars based on verified Google reviews
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {homeTestimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.id || testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: (index % 6) * 0.1 }}
                data-testid={`testimonial-preview-${index}`}
                className="testimonial-card"
              >
                <div className="flex mb-3">
                  {[...Array(testimonial.rating || 5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-muted-foreground text-sm leading-relaxed mb-4">
                  "{testimonial.content}"
                </p>
                <div>
                  <p className="font-serif font-semibold text-primary">{testimonial.name}</p>
                  <p className="text-sm text-muted-foreground">{testimonial.relation}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-center mt-12"
          >
            <Link to="/testimonials">
              <Button
                data-testid="view-all-testimonials-btn"
                variant="outline"
                className="bg-white text-primary border-2 border-primary/20 hover:border-primary hover:bg-primary/5 rounded-full px-8 py-6 text-lg font-medium transition-all"
              >
                Read More Reviews
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
